import type { ReactElement } from 'react';

import type { AiModel, AiModelId, AiModelSettings, AiReasoningEffort, AiVerbosity } from '@mtgcraft/shared';
import { AI_MODEL_IDS, AI_MODELS, AI_VERBOSITIES, closestSupportedEffort } from '@mtgcraft/shared';

import Dropdown from '@/components/Dropdown/Dropdown';

export interface AiModelControlsProps {
  value: AiModelSettings;
  onChange: (value: AiModelSettings) => void;
  defaults: AiModelSettings;
  disabled?: boolean;
}

const MODEL_OPTIONS = AI_MODEL_IDS.map((id) => ({ value: id, label: `${AI_MODELS[id].label} · ${id}` }));
const VERBOSITY_OPTIONS = AI_VERBOSITIES.map((verbosity) => ({ value: verbosity, label: verbosity }));
const MAX_OUTPUT_PRESETS = [1_000, 2_000, 4_000, 8_000, 16_000, 32_000];

function maxOutputOptions(current: number) {
  const values = MAX_OUTPUT_PRESETS.includes(current) ? MAX_OUTPUT_PRESETS : [...MAX_OUTPUT_PRESETS, current].sort((a, b) => a - b);
  return values.map((tokens) => ({ value: String(tokens), label: tokens.toLocaleString() }));
}

function isDefault(value: AiModelSettings, defaults: AiModelSettings): boolean {
  return (
    value.model === defaults.model &&
    value.effort === defaults.effort &&
    value.verbosity === defaults.verbosity &&
    value.maxOutputTokens === defaults.maxOutputTokens
  );
}

/** Admin-only overrides so model settings can be compared without a redeploy. */
export default function AiModelControls({ value, onChange, defaults, disabled }: AiModelControlsProps): ReactElement {
  const model: AiModel = AI_MODELS[value.model];
  const { pricing } = model;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
        <label className="space-y-1 sm:col-span-2">
          <span className="text-ash block text-xs">Model</span>
          <Dropdown
            value={value.model}
            options={MODEL_OPTIONS}
            onChange={(id) => {
              if (!id) return;
              const next = id as AiModelId;
              onChange({ ...value, model: next, effort: closestSupportedEffort(next, value.effort) });
            }}
            size="sm"
            disabled={disabled}
            aria-label="Model"
          />
        </label>

        <label className="space-y-1">
          <span className="text-ash block text-xs">Reasoning</span>
          <Dropdown
            value={value.effort}
            options={model.reasoningEfforts.map((effort) => ({ value: effort, label: effort }))}
            onChange={(effort) => effort && onChange({ ...value, effort: effort as AiReasoningEffort })}
            size="sm"
            disabled={disabled}
            aria-label="Reasoning effort"
          />
        </label>

        <label className="space-y-1">
          <span className="text-ash block text-xs">Verbosity</span>
          <Dropdown
            value={value.verbosity}
            options={VERBOSITY_OPTIONS}
            onChange={(verbosity) => verbosity && onChange({ ...value, verbosity: verbosity as AiVerbosity })}
            size="sm"
            disabled={disabled}
            aria-label="Output verbosity"
          />
        </label>

        <label className="space-y-1">
          <span className="text-ash block text-xs">Max output</span>
          <Dropdown
            value={String(value.maxOutputTokens)}
            options={maxOutputOptions(value.maxOutputTokens)}
            onChange={(tokens) => tokens && onChange({ ...value, maxOutputTokens: Number(tokens) })}
            size="sm"
            disabled={disabled}
            aria-label="Max output tokens"
          />
        </label>
      </div>

      <div className="text-ash flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-mono" title="USD per million tokens: fresh input · cached input · cache write · output">
          {`$${pricing.inputPerMTok} · $${pricing.cachedInputPerMTok} · ${pricing.cacheWritePerMTok === null ? '—' : `$${pricing.cacheWritePerMTok}`} · $${pricing.outputPerMTok} /MTok`}
        </span>
        <span>{model.description}</span>
        {!isDefault(value, defaults) && (
          <button
            type="button"
            onClick={() => onChange(defaults)}
            disabled={disabled}
            className="text-aether hover:text-aether/80 focus-ring rounded disabled:opacity-50"
          >
            Reset to defaults
          </button>
        )}
      </div>
    </div>
  );
}
